import {
    getSplitPeriodDuration,
    type IState
} from "../helpers";
import { isWorkdayReadyForReset } from "../hosCalculator";
import { evaluateSymmetricSplit, recalculateWorkdayViolationsForCurrentWorkdaySymmetric } from "./symmetric";
import { evaluateAsymmetricSplit, recalculateWorkdayViolationsForCurrentWorkdayAsymmetric } from "./asymmetric";
import { isSplitCompleteBlockedByViolation } from "./shared";

export const evaluateSplit = (state: IState) => {
    if (state.day.deferralStatus === "DayOne" || state.day.deferralStatus === "DayTwo") {
        return;
    }
    const geometry = state.intermediateSplitConfig.geometry;
    // Canada and US Passenger share one symmetric evaluator; their behavior differs
    // only via the per-family matrix props on state.intermediateSplitConfig.
    if (geometry === "symmetric") {
        evaluateSymmetricSplit(state);
    } else if (geometry === "asymmetric") {
        evaluateAsymmetricSplit(state);
    }
    // geometry === undefined → no-op (matches prior switch default for noSplit rulesets)
};

const getOffDutySplitPeriodFromStandardWorkdayReset = (state: IState) => {
    const splitConfig = state.propertySplitSubConfig;
    if (
        state.workday.completedSplitSB ||
        !splitConfig ||
        state.workday.consecutiveOffCount <= 0 ||
        !state.workday.offDutyMinimum
    ) {
        return undefined;
    }

    const splitEligibleOffStart = state.workday.splitEligibleConsecutiveOffStartTime;
    if (!splitEligibleOffStart) {
        return undefined;
    }

    const consecutiveOffStart = state.workday.consecutiveOffStartTime ?? new Date(state.nextDate.getTime() - state.workday.consecutiveOffCount);
    const offDutyMinimum = state.workday.offDutyMinimum;

    // The split tracker is recreated by startNewWorkday the moment the rest reaches the workday minimum, so a
    // long sleeper berth earlier in a rest that keeps going is only visible through the persisted workday counter.
    const hasLongSleeperBerthLeg =
        (state.workday.longestSleeperBerthInConsecutiveOff ?? 0) >= splitConfig.bigSplitMin ||
        state.workday.propertySplitSB?.sbPeriods.some(
            period =>
                period.fromDate >= consecutiveOffStart &&
                period.toDate <= state.nextDate &&
                getSplitPeriodDuration(period) >= splitConfig.bigSplitMin
        );
    if (hasLongSleeperBerthLeg) {
        return undefined;
    }

    // Any rest at least the workday minimum can carry, however long it ran and however many OFF logs
    // it was recorded as. The pairing only affects the 14h window, so a cycle restart is not special here.
    const periodDuration = state.nextDate.getTime() - consecutiveOffStart.getTime();
    if (!state.workday.hasStarted && periodDuration < offDutyMinimum) {
        return undefined;
    }

    // The candidate is only the final offDutyMinimum of the rest, so a WT segment before that window is
    // irrelevant, while any WT inside it still disqualifies the candidate.
    const candidateStart = new Date(state.nextDate.getTime() - offDutyMinimum);
    if (candidateStart.getTime() < splitEligibleOffStart.getTime()) {
        return undefined;
    }
    return { fromDate: candidateStart, toDate: new Date(state.nextDate.getTime()) };
};

const isResetSplitPairingFixDisabled = (state: IState) =>
    state.customCodes.disableUsaPropertyResetSplitPairingFix || state.customCodes.enableUsaPreventWorkdayResetPairingForward;

// Split periods during a workday reset may be able to pair with a future split period in the next workday
// eslint-disable-next-line complexity
export const checkSplitPeriodsDuringWorkdayReset = (state: IState) => {
    const geometry = state.intermediateSplitConfig.geometry;
    if (geometry === "asymmetric") {
        if (!state.workday.completedSplitSB && state.intermediateSplitConfig.failedSplitRecalculatesWorkday) {
            recalculateWorkdayViolationsForCurrentWorkdayAsymmetric(state);
        }
        if (isWorkdayReadyForReset(state)) {
            state.workday.offDutySplitPeriodFromStandardWorkdayReset = isResetSplitPairingFixDisabled(state)
                ? undefined
                : getOffDutySplitPeriodFromStandardWorkdayReset(state);
            state.workday.completedSplitSB = false;
            // reset splitSBDuringReset as it can only be used for the current workday
            if (state.workday.hasStarted) {
                state.splitSBDuringReset = 0;
            }
            let sbPeriods = state.workday.propertySplitSB?.sbPeriods;
            // startNewWorkday recreates the split tracker the moment the rest reaches the workday minimum, so a
            // sleeper berth straddling that moment is only whole in the persisted counter while the rest continues.
            const persistedSleeperBerthDuringReset =
                !isResetSplitPairingFixDisabled(state) && !state.workday.hasStarted ? state.workday.longestSleeperBerthInConsecutiveOff ?? 0 : 0;
            if ((sbPeriods && sbPeriods.length > 0) || persistedSleeperBerthDuringReset > 0) {
                // keep track of the SB period during the workday reset
                let sbPeriodCount = Math.max(persistedSleeperBerthDuringReset, ...(sbPeriods ?? []).map(o => getSplitPeriodDuration(o)));
                if (state.propertySplitSubConfig && sbPeriodCount >= state.propertySplitSubConfig.bigSplitMin) {
                    // FMCSA FAQ05 (MYG-131546): a qualifying workday reset cannot also pair forward as a split.
                    // Suppress carry-forward for USA Property when enabled.
                    if (!state.customCodes.enableUsaPreventWorkdayResetPairingForward) {
                        state.splitSBDuringReset = sbPeriodCount;
                    }
                }
            }
        }
    } else if (geometry === "symmetric") {
        if (state.intermediateSplitConfig.failedSplitRecalculatesWorkday) {
            // Existing "unfreeze on failed split" path (gated by !disableUnfreezeSBSplitNotCompleteFix).
            // Runs the recompute and then the carry-forward/pairing bookkeeping. Unchanged.
            // NOTE (MYG-77582 finding): disableUnfreezeSBSplitNotCompleteFix is enabled in production, so
            // this whole arm is inert there — which means the MYG-131546 USA Passenger carry-forward
            // suppression below (enableUsaPreventWorkdayResetPairingForward) is ALSO inert in production.
            // Only USA Property (asymmetric branch above) exercises that suppression today. Tracked for a
            // separate MYG-131546 follow-up.
            recalculateWorkdayViolationsForCurrentWorkdaySymmetric(state);
            if (isWorkdayReadyForReset(state)) {
                state.workday.completedSplitSB = false;
                // reset splitSBDuringReset as it can only be used for the current workday
                if (state.workday.hasStarted) {
                    state.splitSBDuringReset = 0;
                }
                let sbPeriods = state.workday.splitSB?.periods;
                if (sbPeriods && sbPeriods.length > 0) {
                    let startOfConsecutiveOffDuty = new Date(state.nextDate.getTime() - state.workday.consecutiveOffCount);
                    let eligibleSbPeriods = sbPeriods.filter(o => o.fromDate.getTime() >= startOfConsecutiveOffDuty.getTime());
                    let largestSbPeriod = Math.max.apply(
                        Math,
                        eligibleSbPeriods.map(o => getSplitPeriodDuration(o))
                    );
                    let splitSubConfig = state.config.splitConfig?.type === "symmetric" ? state.config.splitConfig : undefined;
                    if (largestSbPeriod && splitSubConfig?.splitMinimum && largestSbPeriod >= splitSubConfig.splitMinimum) {
                        // FMCSA FAQ05 (MYG-131546): a qualifying workday reset cannot also pair forward as a split.
                        // Suppress carry-forward for USA Passenger only; Canada (incl. mismapped North of 60/Alberta)
                        // is unchanged. Within this symmetric branch, !isCanada ⟺ USA Passenger.
                        if (!(state.customCodes.enableUsaPreventWorkdayResetPairingForward && !state.config.isCanada)) {
                            state.splitSBDuringReset = largestSbPeriod;
                        }
                    }
                }
            }
        } else if (state.intermediateSplitConfig.canadaViolationRecalcOnViolation && isSplitCompleteBlockedByViolation(state)) {
            // MYG-77582 (permanent since MYG-140472): the "unfreeze on failed split" path above is
            // disabled in production (disableUnfreezeSBSplitNotCompleteFix is enabled everywhere),
            // which would otherwise leave Canada's Row B recompute unreachable. Give Canada its own
            // trigger: when a Canadian driver is in violation mid-split, run ONLY the backdating
            // recompute — no carry-forward (that belongs to the unfreeze family). Entering with an
            // active violation makes the recompute treat the split as blocked, so it skips the
            // extension/pairing and backdates the Workday violation to the un-extended end (Row B).
            recalculateWorkdayViolationsForCurrentWorkdaySymmetric(state);
        }
    }
};

export { createSplitSB, getRemainingSplitDuration, hasPartialSplitForCurrentWorkdaySymmetric, unfreezePriorSplitPeriods } from "./shared";
